import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// Define types
export interface VideoMetadata {
  duration: number;
  resolution: {
    width: number;
    height: number;
  };
  format: string;
}

export enum VideoStatus {
  Uploaded = 'Uploaded',
  Processing = 'Processing',
  PreviewReady = 'Preview Ready',
  Published = 'Published',
  Archived = 'Archived'
}

export enum ContentType {
  ShortFilm = 'ShortFilm',
  Educational = 'Educational',
  Marketing = 'Marketing'
}

export enum ToneType {
  Serious = 'Serious',
  Fun = 'Fun'
}

export interface Video {
  id: string;
  user_id: string;
  title: string;
  description: string;
  content_type: ContentType;
  tone: ToneType;
  original_url: string;
  processed_url?: string;
  preview_url?: string;
  status: VideoStatus;
  videostatus?: 'original' | 'processed';
  created_at: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  metadata: VideoMetadata;
  optimization_rules: any[];
  edit_actions: any[];
  performance_metrics?: any;
  processing_warnings?: string[];
  applied_rule_ids?: string[];
}

// Define initial state
interface VideoState {
  videos: Video[];
  currentVideo: Video | null;
  selectedVideo: Video | null;
  loading: boolean;
  error: string | null;
  processingStatus: {
    isProcessing: boolean;
    progress: number;
    message: string;
  };
}

const initialState: VideoState = {
  videos: [],
  currentVideo: null,
  selectedVideo: null,
  loading: false,
  error: null,
  processingStatus: {
    isProcessing: false,
    progress: 0,
    message: ''
  }
};

// Add debouncing and caching
let lastFetchTimestamp = 0;
const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between fetches

export const fetchVideos = createAsyncThunk(
  'videos/fetchVideos',
  async (_, { rejectWithValue }) => {
    try {
      // Check if we're within cooldown period
      const now = Date.now();
      if (now - lastFetchTimestamp < FETCH_COOLDOWN) {
        console.log('Skipping fetch - within cooldown period');
        return rejectWithValue('Rate limit cooldown');
      }
      
      lastFetchTimestamp = now;
      
      const response = await apiService.get('/videos');
      console.log('API Response:', response.data); // For debugging
      
      // Check if response data has the expected structure
      if (response.data?.status !== 'success') {
        throw new Error('Failed to fetch videos: Invalid response format');
      }
      
      // Safely access videos with fallback to empty array
      const videosData = response.data?.data?.videos || [];
      
      if (!Array.isArray(videosData)) {
        console.error('Videos data is not an array:', videosData);
        return []; // Return empty array to prevent mapping errors
      }
      
      return videosData.map((video: any) => ({
        id: video.id || '',
        user_id: video.user_id || '',
        title: video.title || '',
        description: video.description || '',
        content_type: video.content_type || ContentType.Educational,
        tone: video.tone || ToneType.Serious,
        original_url: video.original_url || '',
        processed_url: video.processed_url || '',
        preview_url: video.preview_url || '',
        status: video.status || VideoStatus.Uploaded,
        videostatus: video.videostatus || 'original',
        created_at: video.created_at || new Date().toISOString(),
        metadata: video.metadata || { 
          duration: 0, 
          resolution: { width: 0, height: 0 }, 
          format: '' 
        },
        optimization_rules: video.optimization_rules || [],
        edit_actions: video.edit_actions || []
      }));
    } catch (error: any) {
      const functionName = error.functionName || 'fetchVideos';
      console.error(`Error in ${functionName}:`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to fetch videos'}`);
    }
  }
);

export const uploadVideo = createAsyncThunk(
  'videos/uploadVideo',
  async (videoData: FormData, { rejectWithValue }) => {
    try {
      // Upload video through API
      const response = await apiService.uploadFile(
        '/videos/upload', 
        videoData.get('video') as File,
        videoData.get('content_type_id')?.toString() || 'default'
      );
      return response.data.data;
    } catch (error: any) {
      const functionName = error.functionName || 'uploadVideo';
      console.error(`Error in ${functionName}:`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to upload video'}`);
    }
  }
);

export const processVideo = createAsyncThunk(
  'videos/processVideo',
  async (videoId: string, { rejectWithValue }) => {
    try {
      // Process video through API
      const response = await apiService.post(`/videos/${videoId}/process`);
      return { videoId, ...response.data.data };
    } catch (error: any) {
      const functionName = error.functionName || 'processVideo';
      console.error(`Error in ${functionName} (${videoId}):`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to process video'}`);
    }
  }
);

export const cancelVideoProcessing = createAsyncThunk(
  'videos/cancelVideoProcessing',
  async (videoId: string, { rejectWithValue }) => {
    try {
      // Cancel video processing through API
      const response = await apiService.post(`/videos/${videoId}/cancel`);
      return { videoId, ...response.data.data };
    } catch (error: any) {
      const functionName = error.functionName || 'cancelVideoProcessing';
      console.error(`Error in ${functionName} (${videoId}):`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to cancel video processing'}`);
    }
  }
);

export const deleteVideo = createAsyncThunk(
  'videos/deleteVideo',
  async (videoId: string, { rejectWithValue }) => {
    try {
      // Delete video through API
      await apiService.delete(`/videos/${videoId}`);
      return videoId;
    } catch (error: any) {
      const functionName = error.functionName || 'deleteVideo';
      console.error(`Error in ${functionName} (${videoId}):`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to delete video'}`);
    }
  }
);

export const fetchVideoById = createAsyncThunk(
  'videos/fetchVideoById',
  async (videoId: string, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/videos/${videoId}`);
      
      if (response.data?.status !== 'success') {
        throw new Error('Failed to fetch video');
      }
      
      // The video is directly in response.data.data, not in response.data.data.video
      return response.data.data;
    } catch (error: any) {
      const functionName = error.functionName || 'fetchVideoById';
      console.error(`Error in ${functionName} (${videoId}):`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to fetch video'}`);
    }
  }
);

// Add new thunk for completing video processing
export const completeVideoProcessing = createAsyncThunk(
  'videos/completeVideoProcessing',
  async ({ videoId, processedUrl }: { videoId: string, processedUrl: string }, { rejectWithValue }) => {
    try {
      const response = await apiService.post(`/videos/${videoId}/process/complete`, { processed_url: processedUrl });
      
      if (response.data?.status !== 'success') {
        throw new Error('Failed to complete video processing');
      }
      
      return response.data.data.video;
    } catch (error: any) {
      const functionName = error.functionName || 'completeVideoProcessing';
      console.error(`Error in ${functionName} (${videoId}):`, error);
      return rejectWithValue(`[${functionName}] ${error instanceof Error ? error.message : 'Failed to complete video processing'}`);
    }
  }
);

// Create slice
const videoSlice = createSlice({
  name: 'videos',
  initialState,
  reducers: {
    setCurrentVideo: (state, action: PayloadAction<Video>) => {
      state.currentVideo = action.payload;
    },
    clearCurrentVideo: (state) => {
      state.currentVideo = null;
    },
    updateProcessingProgress: (state, action: PayloadAction<number>) => {
      state.processingStatus.progress = action.payload;
    },
    setProcessingMessage: (state, action: PayloadAction<string>) => {
      state.processingStatus.message = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch videos
      .addCase(fetchVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = action.payload;
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Upload video
      .addCase(uploadVideo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.loading = false;
        state.videos.push(action.payload);
        state.currentVideo = action.payload;
      })
      .addCase(uploadVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Process video
      .addCase(processVideo.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.processingStatus.isProcessing = true;
        state.processingStatus.progress = 0;
        state.processingStatus.message = 'Starting video processing...';
      })
      .addCase(processVideo.fulfilled, (state, action) => {
        state.loading = false;
        state.processingStatus.isProcessing = true;
        state.processingStatus.progress = 10;
        state.processingStatus.message = 'Processing video...';
        
        // Update the selected video status if it's the one being processed
        if (state.selectedVideo && state.selectedVideo.id === action.payload.videoId) {
          state.selectedVideo.status = VideoStatus.Processing;
          state.selectedVideo.processing_started_at = new Date().toISOString();
        }
        
        // Also update in the videos array
        const videoIndex = state.videos.findIndex(v => v.id === action.payload.videoId);
        if (videoIndex !== -1) {
          state.videos[videoIndex].status = VideoStatus.Processing;
          state.videos[videoIndex].processing_started_at = new Date().toISOString();
        }
      })
      .addCase(processVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.processingStatus.isProcessing = false;
        state.processingStatus.message = 'Processing failed';
      })
      // Delete video
      .addCase(deleteVideo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = state.videos.filter(v => v.id !== action.payload);
        
        // Clear current video if it's the one being deleted
        if (state.currentVideo?.id === action.payload) {
          state.currentVideo = null;
        }
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch video by ID
      .addCase(fetchVideoById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideoById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedVideo = action.payload;
      })
      .addCase(fetchVideoById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Cancel video processing
      .addCase(cancelVideoProcessing.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.processingStatus.message = 'Canceling video processing...';
      })
      .addCase(cancelVideoProcessing.fulfilled, (state, action) => {
        state.loading = false;
        state.processingStatus.isProcessing = false;
        state.processingStatus.progress = 0;
        state.processingStatus.message = 'Processing canceled';
        
        // Update the selected video status if it's the one being canceled
        if (state.selectedVideo && state.selectedVideo.id === action.payload.videoId) {
          state.selectedVideo.status = VideoStatus.Uploaded;
          state.selectedVideo.processing_started_at = undefined;
        }
        
        // Also update in the videos array
        const videoIndex = state.videos.findIndex(v => v.id === action.payload.videoId);
        if (videoIndex !== -1) {
          state.videos[videoIndex].status = VideoStatus.Uploaded;
          state.videos[videoIndex].processing_started_at = undefined;
        }
      })
      .addCase(cancelVideoProcessing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.processingStatus.message = 'Cancellation failed';
      })
      // Complete video processing
      .addCase(completeVideoProcessing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeVideoProcessing.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update the video in the videos array
        const index = state.videos.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.videos[index] = action.payload;
        }
        
        // Update current video if it's the one being processed
        if (state.currentVideo?.id === action.payload.id) {
          state.currentVideo = action.payload;
        }
        
        state.processingStatus.isProcessing = false;
        state.processingStatus.progress = 100;
        state.processingStatus.message = 'Processing completed';
      })
      .addCase(completeVideoProcessing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.processingStatus.isProcessing = false;
      });
  }
});

export const { 
  setCurrentVideo, 
  clearCurrentVideo,
  updateProcessingProgress,
  setProcessingMessage
} = videoSlice.actions;

export default videoSlice.reducer; 